import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { 
  Menu, X, Home, User, Settings, FileText, LayoutDashboard,
  Users, Calendar, MessageSquare, Bell, LogOut, Star, Gift,
  HelpCircle, Shield, Globe, Crown, Mail
} from 'lucide-react';
import FeedbackReminder from './FeedbackReminder';

export default function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(AuthContext);

  // Stable close handler
  const closeSidebar = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Lock body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Auto-close sidebar on resize past desktop breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isOpen) {
        closeSidebar();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, closeSidebar]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeSidebar();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeSidebar]);

  const handleNavigate = (path) => {
    closeSidebar();
    navigate(path);
  };

  const handleLogout = () => {
    closeSidebar();
    localStorage.removeItem('token');
    setUser(null);
    navigate('/auth');
  };

  const openSidebar = () => {
    setIsOpen(true);
  };

  const menuItems = [
    { label: 'Home', icon: Home, path: '/home', requiresAuth: false },
    { label: 'Base Camp', icon: User, path: '/', requiresAuth: true },
    { label: 'Inbox', icon: Mail, path: '/inbox', requiresAuth: true },
    { label: 'My Moments', icon: FileText, path: '/my-posts', requiresAuth: true },
    { label: 'Settings', icon: Settings, path: '/settings', requiresAuth: true },
    { divider: true },
    { label: 'Browse', icon: Users, path: '/discover', requiresAuth: false },
    { label: 'Events', icon: Calendar, path: '/events', requiresAuth: false },
    { label: 'Notes', icon: MessageSquare, path: '/messages', requiresAuth: true },
    { label: 'Notifications', icon: Bell, path: '/notifications', requiresAuth: true },
    { divider: true },
    { label: 'Referrals', icon: Gift, path: '/referrals', requiresAuth: true },
    { label: 'Badges', icon: Star, path: '/badges', requiresAuth: true },
    { divider: true },
    { label: 'Help & Support', icon: HelpCircle, path: '/support', requiresAuth: false },
    { label: 'Privacy Policy', icon: Shield, path: '/privacy', requiresAuth: false },
  ];

  // Beta feedback menu entry removed — destination route unrouted pre-deploy.

  // Add admin/founder link prominently for admin users
  if (user?.is_admin) {
    // Insert founder dashboard at the top after Home
    menuItems.splice(1, 0, {
      label: '👑 Founder Dashboard', icon: Crown, path: '/admin/beta', requiresAuth: true, isAdmin: true
    });
  }

  return (
    <>
      {/* Hamburger Button */}
      <button
        onClick={openSidebar}
        className="p-2 rounded-xl border-2 border-black bg-white hover:bg-slate-100 transition-colors shadow-brutal"
        aria-label="Open menu"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Overlay - click to close, with higher z-index to escape stacking context */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50"
          style={{ zIndex: 9998 }}
          onClick={closeSidebar}
          onTouchEnd={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar - above overlay with even higher z-index */}
      <div 
        className={`fixed top-0 left-0 h-full w-72 bg-white border-r-4 border-black transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ zIndex: 9999 }}
      >
        {/* Header */}
        <div className="p-4 border-b-2 border-black flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🤝</span>
            <span className="font-bold text-xl">LikeKind</span>
          </div>
          <button
            onClick={closeSidebar}
            className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        {user && (
          <div className="p-4 border-b-2 border-black bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-12 h-12 rounded-full border-2 border-black object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full border-2 border-black bg-indigo-500 flex items-center justify-center text-white font-bold text-lg">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{user.name}</p>
                <p className="text-sm text-slate-600 truncate">@{user.username || 'user'}</p>
              </div>
            </div>
            {user.membership_tier && (
              <div className="mt-2">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  user.membership_tier === 'founder' ? 'bg-yellow-100 text-yellow-700' :
                  user.membership_tier.startsWith('beta') ? 'bg-purple-100 text-purple-700' :
                  user.membership_tier === 'trial' ? 'bg-blue-100 text-blue-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  {user.membership_tier === 'founder' ? '👑 Founder' :
                   user.membership_tier.startsWith('beta') ? '🧪 Beta Tester' :
                   user.membership_tier === 'trial' ? '⏱️ Trial' :
                   '✨ ' + user.membership_tier}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Menu Items */}
        <nav className="p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {menuItems.map((item, index) => {
            if (item.divider) {
              return <div key={index} className="my-2 border-t border-slate-200" />;
            }

            if (item.requiresAuth && !user) {
              return null;
            }

            const isActive = location.pathname === item.path || 
                           (item.path.includes('/profile/') && location.pathname.includes('/profile/')) ||
                           (item.path === '/me' && location.pathname === '/me');

            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                  item.isAdmin 
                    ? 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 font-bold border-2 border-amber-300 hover:from-amber-200 hover:to-yellow-200'
                    : isActive 
                      ? 'bg-indigo-100 text-indigo-700 font-semibold' 
                      : 'hover:bg-slate-100 text-slate-700'
                }`}
              >
                <item.icon className={`w-5 h-5 ${item.isAdmin ? 'text-amber-600' : ''}`} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t-2 border-black bg-white space-y-3">
          {/* Beta Feedback Reminder in Sidebar */}
          {user && <FeedbackReminder compact />}
          
          {user ? (
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors font-semibold"
            >
              <LogOut className="w-5 h-5" />
              <span>Log Out</span>
            </button>
          ) : (
            <button
              onClick={() => handleNavigate('/auth')}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-500 text-white hover:bg-indigo-600 transition-colors font-semibold border-2 border-black shadow-brutal"
            >
              <User className="w-5 h-5" />
              <span>Sign In</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
